// File: src/pages/ApprovalManager/ApprovalManagerLeaveApprovals.js
/*
 * BRAND NEW FILE — Did not exist in original
 * -----------------------------------------------------------------------
 * API:
 *   GET  /api/leaves/pending/approval-manager
 *   PUT  /api/leaves/approval-manager/action/{id}?newStatus=APPROVED&remark=...
 *
 * Shows all leave requests pending AM action.
 * AM remark is passed to cluster manager as VIEW ONLY after forwarding.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Button, TextField, Stack, Chip, TableContainer,
  CircularProgress, Alert, Modal
} from '@mui/material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const MODAL_STYLE = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420, bgcolor: 'background.paper',
  boxShadow: 24, p: 4, borderRadius: 3
};

const ApprovalManagerLeaveApprovals = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, id: null, action: '', remark: '', item: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/leaves/pending/approval-manager');
      setRequests(res.data || []);
    } catch (e) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (id, action, item) =>
    setModal({ open: true, id, action, remark: '', item });

  const submit = async () => {
    if (!modal.remark.trim()) {
      toast.error('Remark is required');
      return;
    }
    try {
      await api.put(`/leaves/approval-manager/action/${modal.id}`, null, {
        params: { newStatus: modal.action, remark: modal.remark }
      });
      toast.success(`Leave ${modal.action === 'APPROVED' ? 'approved (Final) — attendance updated' : 'rejected'}`);
      setModal({ open: false, id: null, action: '', remark: '', item: null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    }
  };

  const maxDays = modal.item
    ? Math.round((new Date(modal.item.endDate) - new Date(modal.item.startDate)) / 86400000) + 1
    : 1;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={800} mb={3}>
        Leave Approvals — First Level
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : requests.length === 0 ? (
        <Alert severity="success" sx={{ borderRadius: 3 }}>No pending leave requests for your employees.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
              <TableRow>
                <TableCell><b>Employee</b></TableCell>
                <TableCell><b>Branch</b></TableCell>
                <TableCell><b>Start Date</b></TableCell>
                <TableCell><b>End Date</b></TableCell>
                <TableCell><b>Days</b></TableCell>
                <TableCell><b>Reason</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Action</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell><b>{r.employeeName || '-'}</b></TableCell>
                  <TableCell>{r.branchName || '-'}</TableCell>
                  <TableCell>{r.startDate || '-'}</TableCell>
                  <TableCell>{r.endDate || '-'}</TableCell>
                  <TableCell>
                    {r.startDate && r.endDate
                      ? Math.round((new Date(r.endDate) - new Date(r.startDate)) / 86400000) + 1
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography fontSize={13}>{r.reason || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={r.status} color="warning" size="small" />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" color="success"
                        onClick={() => openModal(r.id, 'APPROVED', r)}>
                        Approve
                      </Button>
                      <Button size="small" variant="outlined" color="error"
                        onClick={() => openModal(r.id, 'REJECTED', r)}>
                        Reject
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirm Modal */}
      <Modal open={modal.open} onClose={() => setModal({ ...modal, open: false })}>
        <Box sx={MODAL_STYLE}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>
            {modal.action === 'APPROVED' ? '✅ Approve Leave' : '❌ Reject Leave'}
          </Typography>
          {modal.item && (
            <Typography fontSize={13} color="text.secondary" mb={2}>
              {modal.item.employeeName} · {modal.item.startDate} → {modal.item.endDate} ({maxDays} day{maxDays > 1 ? 's' : ''})
            </Typography>
          )}
          <TextField
            fullWidth multiline rows={3}
            label="Your Remark — Final Decision (visible to Cluster Manager in history) *"
            value={modal.remark}
            onChange={e => setModal({ ...modal, remark: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" fullWidth onClick={() => setModal({ ...modal, open: false })}>
              Cancel
            </Button>
            <Button
              variant="contained" fullWidth
              color={modal.action === 'APPROVED' ? 'success' : 'error'}
              disabled={!modal.remark.trim()}
              onClick={submit}
            >
              Confirm {modal.action === 'APPROVED' ? 'Approve' : 'Reject'}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default ApprovalManagerLeaveApprovals;